import React from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { LOAD_UNIT, LOAD_UNIT_LABELS, getLoadUnitsForInputMode } from '../../../enums/programming';
import { SetDetail } from './workoutBuilderTypes';
import styles from './PerSetGrid.module.scss';

/** Metodi che richiedono il campo carico per-serie */
export const VARIABLE_LOAD_METHODS = new Set(['pyramid', 'dropset']);

interface PerSetGridProps {
  setsDetail: SetDetail[];
  /** Se true, mostra la colonna carico (reps + carico per serie variabile) */
  showLoad: boolean;
  /** Unità carico globale usata come fallback per nuove righe */
  globalLoadUnit: LOAD_UNIT;
  /** Input mode dell'esercizio: filtra le unità ammesse (no unità incoerenti). */
  inputMode?: string;
  onChange: (updated: SetDetail[]) => void;
}

const PREFERRED_UNITS = [LOAD_UNIT.KG, LOAD_UNIT.LB, LOAD_UNIT.PERCENT_1RM, LOAD_UNIT.PERCENT_BW];

const PerSetGrid: React.FC<PerSetGridProps> = ({
  setsDetail,
  showLoad,
  globalLoadUnit,
  inputMode,
  onChange,
}) => {
  // Unità ammesse per l'input mode (BUG E): no unità endurance/incoerenti.
  const allowedUnits = getLoadUnitsForInputMode(inputMode);
  const POPULAR_UNITS = PREFERRED_UNITS.filter((u) => allowedUnits.includes(u));
  const OTHER_UNITS = allowedUnits.filter((u) => !PREFERRED_UNITS.includes(u));
  const handleReps = (idx: number, value: string) => {
    const updated = [...setsDetail];
    updated[idx] = { ...updated[idx], reps: value };
    onChange(updated);
  };

  const handleRest = (idx: number, value: string) => {
    const updated = [...setsDetail];
    updated[idx] = { ...updated[idx], rest: value };
    onChange(updated);
  };

  const handleLoadValue = (idx: number, value: string) => {
    const updated = [...setsDetail];
    updated[idx] = { ...updated[idx], loadValue: value };
    onChange(updated);
  };

  const handleLoadUnit = (idx: number, unit: LOAD_UNIT) => {
    const updated = [...setsDetail];
    updated[idx] = { ...updated[idx], loadUnit: unit };
    onChange(updated);
  };

  return (
    <div className={styles.perSetGrid}>
      {/* Header */}
      <div className={styles.headerRow}>
        <span className={styles.cellSet}>Set</span>
        <span className={styles.cellReps}>Reps</span>
        {showLoad && <span className={styles.cellLoad}>Carico</span>}
        {showLoad && <span className={styles.cellUnit}>Unità</span>}
        <span className={styles.cellRest}>Recupero</span>
      </div>

      {/* Rows */}
      {setsDetail.map((sd, idx) => {
        const unit = sd.loadUnit ?? globalLoadUnit;
        return (
          <div key={sd.setNumber} className={styles.dataRow}>
            <span className={styles.cellSet}>{sd.setNumber}</span>

            <div className={styles.cellReps}>
              <input
                type="text"
                className={styles.input}
                value={sd.reps ?? ''}
                placeholder="—"
                onChange={(e) => handleReps(idx, e.target.value)}
              />
            </div>

            {showLoad && (
              <div className={styles.cellLoad}>
                <input
                  type="text"
                  className={styles.input}
                  value={sd.loadValue ?? ''}
                  placeholder="—"
                  onChange={(e) => handleLoadValue(idx, e.target.value)}
                />
              </div>
            )}

            {showLoad && (
              <div className={styles.cellUnit}>
                <div className={styles.selectWrapper}>
                  <select
                    className={styles.unitSelect}
                    value={unit}
                    onChange={(e) => handleLoadUnit(idx, e.target.value as LOAD_UNIT)}
                  >
                    <optgroup label="Più usate">
                      {POPULAR_UNITS.map((u) => (
                        <option key={u} value={u}>
                          {LOAD_UNIT_LABELS[u]}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Altre">
                      {OTHER_UNITS.map((u) => (
                        <option key={u} value={u}>
                          {LOAD_UNIT_LABELS[u]}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <CaretDown size={10} className={styles.selectIcon} />
                </div>
              </div>
            )}

            <div className={styles.cellRest}>
              <input
                type="text"
                className={styles.input}
                value={sd.rest ?? ''}
                placeholder='90"'
                onChange={(e) => handleRest(idx, e.target.value)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PerSetGrid;
